import React from 'react';
import { 
  Clock, 
  Calendar, 
  AlertTriangle, 
  CheckCircle2, 
  Infinity as InfinityIcon,
  Flame,
  Hourglass
} from 'lucide-react';
import { cn } from './SaaSUIHelper';

export type DeadlineUrgency = 'expired' | 'critical' | 'approaching' | 'moderate' | 'flexible' | 'rolling';

export interface DeadlineStatus {
  daysRemaining: number | null;
  urgency: DeadlineUrgency;
  percentElapsed: number; // 0 to 100
  label: string;
  formattedDate: string;
  badgeClass: string;
  progressClass: string;
  bgTrackClass: string;
  icon: any;
}

export function getDeadlineStatus(dueDateStr?: string, postedDateStr?: string): DeadlineStatus {
  if (!dueDateStr) {
    return {
      daysRemaining: null,
      urgency: 'rolling',
      percentElapsed: 100,
      label: 'Rolling / Open',
      formattedDate: 'Open Submission',
      badgeClass: 'bg-indigo-50 text-indigo-700 border-indigo-200',
      progressClass: 'bg-indigo-500',
      bgTrackClass: 'bg-indigo-100',
      icon: InfinityIcon
    };
  }

  const dueDate = new Date(dueDateStr);
  if (isNaN(dueDate.getTime())) {
    return {
      daysRemaining: null,
      urgency: 'rolling',
      percentElapsed: 100,
      label: 'Open',
      formattedDate: dueDateStr,
      badgeClass: 'bg-slate-50 text-slate-700 border-slate-200',
      progressClass: 'bg-slate-400',
      bgTrackClass: 'bg-slate-100',
      icon: Calendar
    };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate);
  due.setHours(23, 59, 59, 999);

  const diffTime = due.getTime() - today.getTime();
  const daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  // Determine baseline total window (default 30 days if no postedDate)
  let totalWindowDays = 30;
  if (postedDateStr) {
    const postedDate = new Date(postedDateStr);
    if (!isNaN(postedDate.getTime())) {
      const windowMs = due.getTime() - postedDate.getTime();
      const calculatedDays = Math.ceil(windowMs / (1000 * 60 * 60 * 24));
      if (calculatedDays > 0) totalWindowDays = calculatedDays;
    }
  }

  // Calculate elapsed progress percentage
  // If daysRemaining <= 0, elapsed is 100%
  let percentElapsed = 0;
  if (daysRemaining <= 0) {
    percentElapsed = 100;
  } else if (daysRemaining >= totalWindowDays) {
    percentElapsed = 10;
  } else {
    const daysElapsed = totalWindowDays - daysRemaining;
    percentElapsed = Math.min(100, Math.max(5, Math.round((daysElapsed / totalWindowDays) * 100)));
  }

  // Formatting date string nicely (e.g., "Nov 14, 2026")
  const formattedDate = due.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });

  if (daysRemaining < 0) {
    return {
      daysRemaining,
      urgency: 'expired',
      percentElapsed: 100,
      label: `Closed (${Math.abs(daysRemaining)}d ago)`,
      formattedDate,
      badgeClass: 'bg-rose-50 text-rose-700 border-rose-200',
      progressClass: 'bg-rose-500',
      bgTrackClass: 'bg-rose-100',
      icon: AlertTriangle
    };
  }

  if (daysRemaining === 0) {
    return {
      daysRemaining,
      urgency: 'critical',
      percentElapsed: 98,
      label: 'Due Today!',
      formattedDate,
      badgeClass: 'bg-rose-100 text-rose-800 border-rose-300 animate-pulse font-extrabold',
      progressClass: 'bg-rose-600',
      bgTrackClass: 'bg-rose-100',
      icon: Flame
    };
  }

  if (daysRemaining <= 3) {
    return {
      daysRemaining,
      urgency: 'critical',
      percentElapsed: Math.max(85, percentElapsed),
      label: `${daysRemaining}d remaining`,
      formattedDate,
      badgeClass: 'bg-rose-50 text-rose-700 border-rose-200 font-bold',
      progressClass: 'bg-rose-500',
      bgTrackClass: 'bg-rose-100',
      icon: Flame
    };
  }

  if (daysRemaining <= 7) {
    return {
      daysRemaining,
      urgency: 'approaching',
      percentElapsed: Math.max(70, percentElapsed),
      label: `${daysRemaining}d remaining`,
      formattedDate,
      badgeClass: 'bg-amber-50 text-amber-700 border-amber-200 font-bold',
      progressClass: 'bg-amber-500',
      bgTrackClass: 'bg-amber-100',
      icon: Hourglass
    };
  }

  if (daysRemaining <= 14) {
    return {
      daysRemaining,
      urgency: 'moderate',
      percentElapsed: Math.max(45, percentElapsed),
      label: `${daysRemaining}d left`,
      formattedDate,
      badgeClass: 'bg-blue-50 text-blue-700 border-blue-200',
      progressClass: 'bg-blue-500',
      bgTrackClass: 'bg-blue-100',
      icon: Clock
    };
  }

  return {
    daysRemaining,
    urgency: 'flexible',
    percentElapsed: Math.max(15, percentElapsed),
    label: `${daysRemaining}d left`,
    formattedDate,
    badgeClass: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    progressClass: 'bg-emerald-500',
    bgTrackClass: 'bg-emerald-100',
    icon: Calendar
  };
}

interface DeadlineProgressIndicatorProps {
  dueDate?: string;
  postedDate?: string;
  variant?: 'compact' | 'badge-only' | 'detailed' | 'bar-only';
  className?: string;
  showIcon?: boolean;
}

export function DeadlineProgressIndicator({
  dueDate,
  postedDate,
  variant = 'compact',
  className,
  showIcon = true
}: DeadlineProgressIndicatorProps) {
  const status = getDeadlineStatus(dueDate, postedDate);
  const Icon = status.icon;

  if (variant === 'badge-only') {
    return (
      <span className={cn(
        "inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-mono border whitespace-nowrap",
        status.badgeClass,
        className
      )}>
        {showIcon && <Icon className="w-3 h-3 shrink-0" />}
        <span>{status.label}</span>
      </span>
    );
  }

  if (variant === 'bar-only') {
    return (
      <div className={cn("w-full space-y-1", className)} title={`${status.label} (${status.formattedDate})`}>
        <div className={cn("w-full h-1.5 rounded-full overflow-hidden", status.bgTrackClass)}>
          <div 
            className={cn("h-full rounded-full transition-all duration-500", status.progressClass)}
            style={{ width: `${status.percentElapsed}%` }}
          />
        </div>
      </div>
    );
  }

  if (variant === 'detailed') {
    return (
      <div className={cn("bg-white p-3 rounded-xl border border-slate-200 shadow-2xs space-y-2.5", className)}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className={cn("w-7 h-7 rounded-lg flex items-center justify-center border", status.badgeClass)}>
              <Icon className="w-4 h-4" />
            </div>
            <div>
              <p className="text-xs font-bold text-slate-900">{status.formattedDate}</p>
              <p className="text-[10px] text-slate-500 font-medium">Submission Deadline</p>
            </div>
          </div>

          <span className={cn("px-2 py-0.5 text-[10px] font-mono font-bold rounded-md border", status.badgeClass)}>
            {status.label}
          </span>
        </div>

        {/* Visual Progress Bar */}
        <div className="space-y-1">
          <div className="flex justify-between text-[10px] text-slate-400 font-medium">
            <span>Progress / Timeline</span>
            <span className="font-mono">{status.percentElapsed}% Window Elapsed</span>
          </div>
          <div className={cn("w-full h-2 rounded-full overflow-hidden", status.bgTrackClass)}>
            <div 
              className={cn("h-full rounded-full transition-all duration-500", status.progressClass)}
              style={{ width: `${status.percentElapsed}%` }}
            />
          </div>
        </div>
      </div>
    );
  }

  // Default: 'compact' (optimized for table rows and list items)
  return (
    <div className={cn("min-w-[130px] max-w-[160px] space-y-1.5", className)}>
      <div className="flex items-center justify-between gap-1.5">
        <span className="text-[11px] font-bold text-slate-800 font-mono flex items-center gap-1">
          {showIcon && <Icon className={cn("w-3.5 h-3.5 shrink-0", status.urgency === 'critical' ? 'text-rose-500' : 'text-slate-400')} />}
          <span className="truncate">{status.formattedDate}</span>
        </span>
        <span className={cn(
          "text-[9px] font-mono font-bold px-1.5 py-0.2 rounded border shrink-0 whitespace-nowrap",
          status.badgeClass
        )}>
          {status.label}
        </span>
      </div>

      {/* Mini Progress Bar */}
      <div 
        className={cn("w-full h-1.5 rounded-full overflow-hidden shadow-2xs", status.bgTrackClass)}
        title={`Submission Deadline: ${status.formattedDate} • ${status.label} (${status.percentElapsed}% elapsed)`}
      >
        <div 
          className={cn("h-full rounded-full transition-all duration-300", status.progressClass)}
          style={{ width: `${status.percentElapsed}%` }}
        />
      </div>
    </div>
  );
}
